import {DisplayField} from "./display";
import {BulkAction} from './bulk-action';
import { OverviewXlsxColumn } from './overview-xlsx-column';
import { PaymentRoute } from './payment-route';

export interface Settings {
  accounting: AccountingSettings;
  approval_confirmation: ApprovalConfirmationSettings;
  archive: ArchiveSettings;
  authentication: AuthenticationSettings;
  automation: AutomationSettings;
  bulk_actions: BulkActionSettings;
  'case-workers': CaseWorkersSettings;
  comments: CommentsSettings;
  criteria: CriteriaSettings;
  'custom-value'?: CustomValueSettings;
  extra_columns?: ExtraColumnsSettings;
  dialog?: DialogSettings;
  export_project_logs?: ExportProjectLogsSettings;
  forecast: ForecastSettings;
  gdpr?: GdprSettings;
  import?: ImportSettings;
  journal_no?: JournalNumberSettings;
  applicant: ApplicantSettings;
  payments?: PaymentsSettings;
  'payment-receipts': PaymentReceiptsSettings;
  profile: ProfileSettings;
  scoring: ScoringSettings;
  security?: SecuritySettings;
  sms?: SmsSettings;
  smtp?: SmtpSettings;
  support?: SupportSettings;
  recommendations: RecommendationsSettings;
  materialized_view: MaterializedViewSettings;
  'paper-refusal': RefusalSettings;
  languages: string[];
  time_zone: string;
  board: {
    version?: string;
    language?: string;
    languages?: string[];
  };
  votes: VotesSettings;
  overview_xlsx: OverviewXlsxSettings;
  siblings: SiblingsSettings[];
  transfer?: TransferSettings;
  //portal notifications cleanup
  notifications: NotificationsSettings,
  [key: string]: any;
  styling?: StylingSettings
  'split-grants'? : SplitGrantsSettings;
  tag: string;
  employee_no: EmployeeNumberSettings,
  'tax-report'?: TaxSettings;
  ai?: AISettings,
  warnings: WarningsSettings;
}

export interface OverviewXlsxSettings {
  columns: OverviewXlsxColumn[];
}

export interface TransferSettings {
  case_documents?: boolean;
  messages?: boolean;
  recommendation?: boolean;
}

export interface StylingSettings {
  primaryColor: string;
  onPrimary: string;
  secondaryColor: string;
  onSecondary: string;
  primaryContainer: string;
  onPrimaryContainer: string;
  secondaryContainer: string;
  onSecondaryContainer: string;
  warnColor: string;
  // Custom grant styling for buttons (not a part of standard material theming)
  flatButtonColor?: string;
  onFlatButtonColor?: string;
  outline?: string; // border for stroked buttons
  onSecondaryButtonColor?: string;
  selectionColor?: string;
  onSelectionColor?: string;
  headerFontFamily: string;
  bodyFontFamily: string;
  buttonFontFamily?: string;
  backgroundColor: string;
  textColor: string;
  headerTextColor: string;
  matCardColor: string;
  headerWeight: number;
  bodyWeight: number;
  buttonWeight?: number;
}
export type StylingColorSettings = Pick<
  StylingSettings,
  | "primaryColor" | "onPrimary" | "secondaryColor" | "onSecondary" | "primaryContainer" | "onPrimaryContainer"
  | "secondaryContainer" | "onSecondaryContainer" | "warnColor" | "backgroundColor" | "textColor"
  | "headerTextColor" | "matCardColor" | "flatButtonColor" | "onFlatButtonColor" | "outline" | "onSecondaryButtonColor"
  | "selectionColor" | "onSelectionColor"
>;
export type StylingTextSettings = Pick<
  StylingSettings,
  | "headerFontFamily" | "bodyFontFamily" | "buttonFontFamily" | "headerWeight" | "bodyWeight" | "buttonWeight"
>;

export interface AccountingSettings {
  sftp?: {
    host: string;
    user: string;
    key?: string;
    password_secret?: string;
    password_secret_present?: boolean;
  };
  system?: 'generic' | 'uniconta' | 'economic' | 'dynamic' | 'opus' | 'multi_regnskab';
  export_postings?: boolean; // economic + uniconta
  export_posting_attachments?: boolean // economic + bc
  // e-conomic
  token?: string;
  journal_no?: string;
  // MultiRegnskab
  multi_regnskab?: {
    portfolio_customer_id: string;
    authorization: string;
    url: string;
  },
  // Uniconta specific fields
  journal?: string; // uniconta
  company_id?: string;
  username?: string;
  password?: string;
  uniconta_fields?: { name: string, value: string }[];
  // Opus specific settings
  posting_date_as_current_date?: boolean;
  comp_code?: string;
  municipality_no?: string;
  prod_env?: string;
  system_id?: string;
  data_provider_id?: string;
  // Generic w/Navision CSV file format
  csv_options?: any;
  csv_skip_header?: boolean;
  // General
  batch_size?: number;
  payment_credit_account: string;
  payment_debit_account: string;
  provision_account_no: string;
  account_no: string;
  intermediate_account?: string;
  project_provision_account?: boolean;
  same_year_reverse_provision_account?: string;
  special_reverse_provision_account?: string;
  contra_debit_account_outgoing?: string;
  contra_credit_account_outgoing?: string;
  contra_debit_account_incoming?: string;
  contra_credit_account_incoming?: string;
  dimensions?: { name: string, code: string }[];
  dimension_mandatory?: boolean;
  account_type_prefix?: string;
  allow_reset?: boolean; // Internal - Allow client to reset export status
  flip_account_contra?: boolean;
  flip_sign?: boolean;
  'file-format'?: 'PostingsCsv' | 'NavisionPostingsCsv' | 'PostingsXlsx' | 'OpusPostingsXml',
  'file-columns'?: { key:string, value?: string | number, format?: string }[],
  payment_type?: any;
  encoding?: string;
  decimal_places?: number;
  decimal_separator?: string;
  use_local_currency?: boolean;
  settle_exchange_rate_diff?: boolean;
  exchange_rate_diff_credit_account?: string;
  exchange_rate_diff_debit_account?: string;
}

export interface ApprovalConfirmationSettings {
  list_projects: boolean;
  automatic: {
    enabled: boolean;
    grace: number;
  }
}

export interface ArchiveSettings {
  receipts: boolean;
  rejections: boolean;
  approvals: boolean;
  email?: string;
}

export interface AuthenticationSettings {
  modes: ('mitid' | 'nemid' | 'codefile' | 'tfa' | 'sso')[];
}

export interface AutomationSettings {
  triggers?: string[];
  actions?: string[];
}

export type BulkActionOption = {
  buffer: number
}

export type BulkActionOptions = Partial<Record<BulkAction, BulkActionOption>>;

export interface BulkActionSettings {
  actions?: BulkAction[],
  action_options?: BulkActionOptions
}

export interface CaseWorkersSettings {
  remove_delay: number;
  daily_reminder: boolean;
}

export interface CommentsSettings {
  shared: boolean;
}

export interface CriteriaSettings {
  individual: boolean;
  pct_precision?: number;
  allow_manual: boolean;
}

export interface CustomValueSettings {
  decimals: number;
}

export const dialogThreadTypes = ['applicant', 'expert', 'none'] as const;
export type DialogThreadType = typeof dialogThreadTypes[number];

export type DialogSettings = {
  shared_reader: boolean,
  internal_senders: string[],
  thread_type: DialogThreadType
}

export type ForecastDimension = {
  code: string,
  name: string
}

export type ForecastSettings = {
  no_exceeding_available: boolean;
  least_one_source: boolean;
  multiple_sources: boolean;
  allow_change_after_approval: boolean;
  dimensions: {
    [K in number]: ForecastDimension[]
  }
}

export interface ExtraColumnsSettings {
  fields: DisplayField[];
}

export interface GdprSettings {
  refused_and_moved: number;
  old_approved: number;
  newly_approved: number;
  newly_refused: number;
  dangling: number;
  total_history: boolean;
  clear_description_on_clean: boolean;
  dead_drafts: number;
  withdrawn: number;
  inactive_profiles: number;
  attachment_keep_on_clean: boolean;
  message_keep_on_clean: boolean;
  keep_approval_letter: boolean;
  keep_refusal_letter: boolean;
  report_keep_on_clean: boolean;
  mail?: string[];
  purge_enabled: boolean;
  purge_dangling: number;
  purge_refused: number;
  purge_approved: number;
  purge_log: number;
  cleaner_disabled: boolean;
  clear_digest_enabled: boolean;
  clear_digest_period: number;
}

export interface NotificationsSettings {
  cleanup: {
    noticed: number,
    unnoticed: number
  }
}

export const applicantDigests = ['company', 'company_registration_no', 'employee_no', ''] as const;
export type ApplicantSettings = {
  digest: typeof applicantDigests[number];
  force_digest: boolean;
  production_unit_on_top: boolean;
}

export interface EmployeeNumberSettings {
  employeeNoRegex: string;
}

export interface ExportProjectLogsSettings {
  exports_enabled: boolean;
  sftp_host: string;
  sftp_user: string;
  sftp_password: string;
  sftp_path: string;
  providers: ('saml' | 'mitid' | 'otp' | 'mitid_employee' | 'otp_sms')[];
}

export interface PaymentFileColumn {
  key: string;
  width: number;
  style: 'text' | 'date' | 'amount';
  format?: string;
}

export interface PaymentsSettings {
  notify_accounting: string;
  notify_on_incoming: boolean;
  incoming_show_totals: boolean;
  incoming_show_past: boolean;
  notify_on_outgoing: boolean;
  outgoing_show_totals: boolean;
  outgoing_show_past: boolean;
  notify_on_dropped: boolean;
  dropped_show_totals: boolean;
  dropped_show_past: boolean;
  notify_on_initial_addendum: boolean;
  notify_on_additional_addendum: boolean;
  additional_addendum_show_totals: boolean;
  additional_addendum_show_past: boolean;
  notify_on_transfer: boolean;
  transfer_show_totals: boolean;
  transfer_show_past: boolean;
  notify_on_exchange_rate_diff: boolean;
  'file-columns'?: PaymentFileColumn[],
  'file-format': 'NordeaCsv' | 'BusinessOnlineCsv' | 'NykreditCsv' | 'SydbankCsv' | 'PaymentsXlsx' | 'OpusPaymentsTxt',
  notify_applicant: boolean;
  use_p_no: boolean;
  frequency_mode: 'default' | 'apmf',
  frequency: number[];
  skip_weeks?: number[];
  force_future?: boolean;
  export_cutoff: number;
  currency_based_routing?:  Record<string, PaymentRoute>;
}

export interface PaymentReceiptsSettings {
  receipts_amount_check: boolean;
}

export interface ProfileSettings {
  account_alert_on_payment_request: boolean;
  logins: ('email' | 'nemid' | 'mitid')[];
  signups: ('email' | 'nemid' | 'mitid')[];
  force: boolean;
  confirmation: boolean;
  allow_copy_of_rejection: boolean;
  allow_copy_of_approved: boolean;
  allow_share_applications: boolean;
  underage?: number;
  payment_request_notice_days?: number;
  prefer_applicant_nem_konto: boolean;
}

export const recommendationMetas = [
  'journal_no', 'category', 'applied', 'subcategory', 'total', 'applicant', 'period', 'own_funding', 'needed_amount',
  'granted_others', 'other_funds', 'case_workers', 'committee_amount', 'meeting'
] as const;
export type RecommendationMeta = typeof recommendationMetas[number];
export type RecommendationCompactHistory = 'digest' | 'company_registration_no';
export type RecommendationStyleType = 'standard' | 'ap';
export type RecommendationsSettings = {
  warning_multiple_applications: boolean;
  hide_priority: boolean;
  style?: RecommendationStyle | RecommendationStyleType;
  attachment_scale: number;
  show_private_name: boolean;
  show_private_address: boolean;
  compact_history: RecommendationCompactHistory;
  priorities: string[];
  exceeding_grant_warning: boolean;
  individual_print: {
    author: boolean,
    amount: boolean,
    description: boolean,
    spiderweb: boolean,
    history: 'standard' | 'compact'
    meta: RecommendationMeta[]
  }
}
export const recommendationFonts = [
  'Droid Serif', 'Open Sans', 'Open Sans Light', 'Times New Roman', 'Ariel', 'Montserrat', 'Spectral'
] as const;
export type RecommendationFont = typeof recommendationFonts[number];
export const horizontalAlignments = {
  left: 'Venstre',
  right: 'Højre',
  center: 'Centrum'
}
export type HorizontalAlignment = keyof typeof horizontalAlignments;
export const recommendationFontSizes = {
  metadata: { label: 'Metadata', value: 9 },
  content: { label: 'Indhold', value: 9 },
  toc: { label: 'Indholdsfortegnelse', value: 9 },
  recommended_amount: { label: 'Indstillet beløb', value: 9 },
  preamble_box: { label: 'Indlednings data', value: 8 },
  footer_page_number: { label: 'Sidefod side nummer', value: 7 },
  preamble_title: { label: 'Indledning titel', value: 20 },
  category_name: { label: 'Kategori navn', value: 16 },
  heading_project: { label: 'Ansøgnings titel', value: 12 }
} as const
export type RecommendationStyle = {
  footer?: {
    text: string,
    options: {
      align: HorizontalAlignment,
      size: number,
      leading: number,
      color: string,
      character_spacing: number
    }
  },
  font?: RecommendationFont,
  logo?: {
    width: number,
    position: HorizontalAlignment
  },
  font_sizes?: {
    -readonly [K in keyof typeof recommendationFontSizes]?: number
  };
}

export interface RefusalSettings {
  template: string;
}

export interface ScoringSettings {
  range?: {
    label: string,
    value: number
  }[];
}

export type SecuritySettings = {
  mail: string[];
  skip_ip_check: boolean;
  anti_virus: {
    mail: string;
  },
  hazardous_file: {
    mail: string;
    allowed_types: string[];
    notifications: {
      user: boolean;
      profile: boolean;
    }
  }
}

export interface SmsSettings {
  all_messages: boolean;
  approval: boolean;
  refusal: boolean;
  sender: string;
}

export interface SplitGrantsSettings {
  lock_before_notify: boolean;
}

export interface SiblingsSettings {
  subdomain: string,
  share_history: boolean,
  share_profiles: boolean
}

export interface SmtpSettings {
  disable_deliveries: boolean;
  from?: string | null;
  reply_to?: string | null;
}

export interface SupportSettings {
  email: string;
}

export interface TaxSettings {
  report_on_past_year: boolean;
}

export type MaterializedViewSettings = {
  schedule: number,
  project_list_period: number // in months
}

export interface VotesSettings {
  show_details: boolean;
}

export interface ImportSettings {
  show_import_project_statuses: boolean;
}

export interface JournalNumberSettings {
  pattern: string;
  cutover_month: number;
  cutover_day: number;
  continuous_running_number: boolean;
}

export const promptTypes = ['recommendation', 'rating', 'chat'] as const;
export type PromptType = typeof promptTypes[number];
export type AISettings = {
  allow_gdpr_unsafe_files: boolean,
  prompt_types: PromptType[],
}

export type WarningsSettings = {
  exceeding_grant_warning: boolean
}

export type ForeignCurrencySettings = {
  calc_field: 'exchange_rate' | 'amount'
}
